/**
 * Admin — Oval Care Sync. Read-only visibility over the upstream membership
 * sync outbox, plus a SAFE, human-initiated retry (card 843, backend card 842).
 *
 * 🔴 THE DISABLED STATE IS THE NORMAL STATE. `OVAL_CARE_API_ENABLED` ships false,
 * so the expected view today is: banner up, zero rows, every Retry disabled. The
 * flag is read from the SERVER's `integrationEnabled` on every response — never
 * from anything the client could guess — so the banner cannot disagree with the
 * 409 the retry endpoint would actually return.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { listSyncRows, getSyncRow, retrySyncRow } from '../../services/ovalCareSyncService';
import styles from './OvalCareSyncPage.module.css';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_flight', label: 'In flight' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
  { value: 'blocked', label: 'Blocked' }
];

const OPERATION_OPTIONS = [
  { value: 'all', label: 'All operations' },
  { value: 'enroll', label: 'Enroll' },
  { value: 'cancel', label: 'Cancel' },
  { value: 'profile', label: 'Profile' },
  { value: 'billing', label: 'Billing' },
  { value: 'shipping', label: 'Shipping' }
];

const DISABLED_NOTICE =
  'Oval Care integration is disabled — no upstream calls will be made';

const DOMAIN_NOTE =
  'Pet Benefits affiliate integration for Pin Paws pet ID tags, pet profiles and orders, and MetLife pet insurance. This is not DrCare, telehealth, prescriptions, or pharmacy.';

// 🔑 Direction is stated UNCONDITIONALLY. The only other sentence on this page that
// named a direction was DISABLED_NOTICE, which renders ONLY while the integration is
// off — so on an ENABLED box the UI described the data flow nowhere at all.
const DIRECTION_NOTE =
  'One-way, outbound only: Oval queues each membership change here and sends it upstream to Oval Care via quick enrollment. Oval Care does not call back into Oval — there are no inbound vendor webhooks.';

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
      })
    : '—';

const shortId = (id) => (id ? `${id.slice(0, 8)}…` : '—');

const labelFor = (options, value) =>
  options.find((option) => option.value === value)?.label ?? value;

/** Pure. Maps a row status onto its badge class; unknown values fall back to neutral. */
const badgeClass = (status) => styles[`st_${status}`] ?? styles.st_neutral;

/**
 * 🔴 100 is the SERVER'S CEILING, not a preference: `ovalCareSyncController.js:177`
 * clamps `pageSize` to [1, 100], so asking for more is silently clamped and every row
 * past the 100th stays unreachable. Card #28320 — this page used to request 100 and
 * never send `page`, so it rendered 100 rows beneath a label showing the server's real
 * `total` (171 when measured): the operator was told 171 and shown 100, with the OLDEST
 * rows hidden, because the list is ordered `created_at DESC`. Paging is the only way in.
 */
const PAGE_SIZE = 100;

/** Pure. Page count for a row total; never below 1, so the label cannot read "of 0". */
const derivePageCount = (total) => Math.max(1, Math.ceil(total / PAGE_SIZE));

/** Pure. The inclusive, 1-based span of rows currently on screen. */
const deriveRange = (page, total, shown) =>
  total === 0
    ? { start: 0, end: 0 }
    : { start: (page - 1) * PAGE_SIZE + 1, end: (page - 1) * PAGE_SIZE + shown };

const OvalCareSyncPage = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('all');
  const [operation, setOperation] = useState('all');
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // Server-owned. Null until the first response so the banner never flashes the
  // wrong state while loading.
  const [integrationEnabled, setIntegrationEnabled] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [retryError, setRetryError] = useState(null);
  const [retryingId, setRetryingId] = useState(null);

  const loadRows = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await listSyncRows({ status, operation, page, pageSize: PAGE_SIZE });
      setRows(data.items ?? []);
      setTotal(data.total ?? 0);
      setIntegrationEnabled(data.integrationEnabled === true);
    } catch (error) {
      // A real message, never a blank table — an operator must be able to tell
      // "nothing to sync" apart from "this page could not load".
      setLoadError(error?.message || 'Failed to load Oval Care sync rows');
      setRows([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [status, operation, page]);

  useEffect(() => { loadRows(); }, [loadRows]);

  const toggleDetail = useCallback(async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(id);
    setDetail({ loading: true });
    try {
      const data = await getSyncRow(id);
      setDetail({ loading: false, item: data.item });
    } catch (error) {
      setDetail({ loading: false, error: error?.message || 'Failed to load row detail' });
    }
  }, [expandedId]);

  /**
   * ⛔ Never a success toast on a 409. The disabled/terminal-state refusals are
   * the whole point of the safe-retry design, so they are rendered inline on the
   * row that was clicked, where the operator is already looking.
   */
  const handleRetry = useCallback(async (id) => {
    setRetryingId(id);
    setRetryError(null);
    try {
      const data = await retrySyncRow(id);
      setRows((current) => current.map((row) => (row.id === id ? data.item : row)));
      setIntegrationEnabled(data.integrationEnabled === true);
    } catch (error) {
      setRetryError({
        id,
        code: error?.code || null,
        message: error?.message || 'Retry failed'
      });
      if (error?.status === 409 && error?.code === 'OVAL_CARE_DISABLED') {
        setIntegrationEnabled(false);
      }
    } finally {
      setRetryingId(null);
    }
  }, []);

  /**
   * ⛔ A filter change MUST reset to page 1. Without it, filtering while on page 2 asks
   * the server for rows 101+ of a result set that may hold three — an EMPTY table on a
   * NON-empty result (and a 400 past the end, since #28269 made overshoot a client
   * error). That is a new defect a naive paging fix ships under a bugfix, so #28320
   * pins it with a criterion of its own rather than trusting the reset to stay.
   */
  const handleStatusChange = useCallback((event) => {
    setStatus(event.target.value);
    setPage(1);
  }, []);

  const handleOperationChange = useCallback((event) => {
    setOperation(event.target.value);
    setPage(1);
  }, []);

  const isDisabled = integrationEnabled === false;
  // Fail CLOSED on the unknown (null) state: Retry stays inert until the server has
  // affirmatively said the integration is enabled. `=== false` alone left the button
  // LIVE while the flag was still unknown, and on a vendor-armed box Retry dispatches.
  const canRetry = integrationEnabled === true;
  const retryTitle = canRetry
    ? 'Re-arm this row for the sync worker'
    : isDisabled ? DISABLED_NOTICE : 'Integration status not confirmed yet';

  const pageCount = derivePageCount(total);
  const range = deriveRange(page, total, rows.length);

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Pet Benefits <span className={styles.accentText}>Sync</span></h1>
          <p className={styles.subtitle}>
            Oval Care affiliate queue for Pin Paws pet benefits, pet profiles/orders, and MetLife pet insurance.
          </p>
        </div>
        <button type="button" className={styles.refreshBtn} onClick={loadRows} disabled={isLoading}>
          {isLoading ? 'Loading' : 'Refresh'}
        </button>
      </header>

      <section className={styles.domainNote} data-testid="oval-care-domain-note" aria-label="Oval Care domain note">
        {DOMAIN_NOTE}
      </section>

      <section className={styles.directionNote} data-testid="oval-care-direction-note" aria-label="Oval Care data flow direction">
        {DIRECTION_NOTE}
      </section>

      {isDisabled && (
        <div className={styles.disabledBanner} role="status" data-testid="oval-care-disabled-banner">
          {DISABLED_NOTICE}
        </div>
      )}

      <div className={styles.filterBar}>
        <select
          className={styles.filterSelect}
          aria-label="Filter by status"
          value={status}
          onChange={handleStatusChange}
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <select
          className={styles.filterSelect}
          aria-label="Filter by operation"
          value={operation}
          onChange={handleOperationChange}
        >
          {OPERATION_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <span className={styles.countLabel} data-testid="oval-care-row-count">
          {total} {total === 1 ? 'row' : 'rows'}
        </span>
      </div>

      {loadError && (
        <div className={styles.errorBanner} role="alert" data-testid="oval-care-error">
          {loadError}
        </div>
      )}

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Created</th>
              <th>Member</th>
              <th>Operation</th>
              <th>Status</th>
              <th>Attempts</th>
              <th>Next attempt</th>
              <th>External IDs</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !isLoading && !loadError && (
              <tr>
                <td className={styles.empty} colSpan={8} data-testid="oval-care-empty">
                  No Oval Care sync rows yet
                  {isDisabled ? ' — the integration is disabled, so nothing is being queued.' : '.'}
                </td>
              </tr>
            )}
            {rows.map((row) => [
              <tr key={row.id} data-row-id={row.id}>
                <td className={styles.nowrap}>{formatDateTime(row.createdAt)}</td>
                <td>
                  {row.userId
                    ? <Link className={styles.memberLink} to={`/admin/members/${row.userId}`}>{shortId(row.userId)}</Link>
                    : <span className={styles.muted}>—</span>}
                  {row.orderCode && <div className={styles.subCell}>{row.orderCode}</div>}
                </td>
                <td>{labelFor(OPERATION_OPTIONS, row.operation)}</td>
                <td>
                  <span className={`${styles.statusBadge} ${badgeClass(row.status)}`}>
                    {labelFor(STATUS_OPTIONS, row.status)}
                  </span>
                </td>
                <td>{row.attemptCount ?? 0}</td>
                <td className={styles.nowrap}>{formatDateTime(row.nextAttemptAt)}</td>
                <td className={styles.idsCell}>
                  <div>{row.externalMembershipId || <span className={styles.muted}>no membership id</span>}</div>
                  <div>{row.externalOrderId || <span className={styles.muted}>no order id</span>}</div>
                </td>
                <td>
                  <div className={styles.actionBtns}>
                    <button type="button" className={styles.detailsBtn} onClick={() => toggleDetail(row.id)}>
                      {expandedId === row.id ? 'Hide' : 'Details'}
                    </button>
                    <button
                      type="button"
                      className={styles.retryBtn}
                      data-testid="oval-care-retry"
                      disabled={!canRetry || retryingId === row.id}
                      title={retryTitle}
                      onClick={() => handleRetry(row.id)}
                    >
                      {retryingId === row.id ? 'Retrying' : 'Retry'}
                    </button>
                  </div>
                  {retryError?.id === row.id && (
                    <div className={styles.inlineError} role="alert" data-testid="oval-care-retry-error">
                      {retryError.message}{retryError.code ? ` (${retryError.code})` : ''}
                    </div>
                  )}
                </td>
              </tr>,
              expandedId === row.id && (
                <tr key={`${row.id}-detail`} className={styles.detailRow}>
                  <td colSpan={8}>
                    {detail?.loading && <div className={styles.muted}>Loading row detail…</div>}
                    {detail?.error && <div className={styles.inlineError} role="alert">{detail.error}</div>}
                    {detail?.item && (
                      <div className={styles.detailCard}>
                        <h2 className={styles.detailHeading}>Sync row</h2>
                        {[
                          ['Row ID', detail.item.id],
                          ['Member', detail.item.userId],
                          ['Order', detail.item.orderId],
                          ['Order code', detail.item.orderCode],
                          ['Operation', detail.item.operation],
                          ['Status', detail.item.status],
                          ['Attempts', detail.item.attemptCount],
                          ['Last attempt', formatDateTime(detail.item.lastAttemptAt)],
                          ['Next attempt', formatDateTime(detail.item.nextAttemptAt)],
                          ['External membership ID', detail.item.externalMembershipId],
                          ['External order ID', detail.item.externalOrderId],
                          ['Last error', detail.item.lastError],
                          ['Created', formatDateTime(detail.item.createdAt)],
                          ['Updated', formatDateTime(detail.item.updatedAt)]
                        ].map(([label, value]) => (
                          <div className={styles.infoRow} key={label}>
                            <span className={styles.label}>{label}</span>
                            <span className={styles.value} data-field={label}>
                              {value === null || value === undefined || value === '' ? '—' : String(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              )
            ])}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <nav className={styles.pagination} aria-label="Sync row pages">
          <span className={styles.rangeLabel} data-testid="oval-care-row-range">
            Showing {range.start}–{range.end} of {total}
          </span>
          {pageCount > 1 && (
            <div className={styles.pageBtns}>
              <button
                type="button"
                className={styles.pageBtn}
                data-testid="oval-care-prev-page"
                disabled={page <= 1 || isLoading}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </button>
              <span className={styles.pageLabel} data-testid="oval-care-page-label">
                Page {page} of {pageCount}
              </span>
              <button
                type="button"
                className={styles.pageBtn}
                data-testid="oval-care-next-page"
                disabled={page >= pageCount || isLoading}
                onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
              >
                Next
              </button>
            </div>
          )}
        </nav>
      )}
    </div>
  );
};

export default OvalCareSyncPage;
